/**
 * The Analyze screen's one question, asked of every card in a binder:
 * if you sold it tomorrow, would you make money?
 *
 * "Tomorrow" means at the vendor's own selling rate, not at full market. A card
 * bought for $8 and worth $9 is profitable at 100% and a loss at 85%, and the
 * rate is how a vendor actually sells, so it decides.
 *
 * Every card lands in one of three lists:
 *   profit  today's price at your rate is at or above what you paid
 *   loss    it is below what you paid
 *   ask     nobody knows what was paid (or there is no price yet), so a loss
 *           could be hiding here
 *
 * Each card also gets one short note on timing, from the last two months of
 * sales: climbing (and where that points next month), swinging, dropping, or
 * steady. The note is advice; the list is the answer.
 */

export const DEFAULT_RATE = 0.9;
export const RATES = [0.8, 0.9, 1];

/** A month-on-month move smaller than this is noise, not a direction. */
const MOVE = 0.03;
/** Fewer sales than this in a month is an anecdote, not a price. */
const MIN_SALES = 2;
/** Held this long and still losing money, it is taking up a table. */
const STALE_DAYS = 90;

const DAY = 86400000;

export const round2 = (n) => Math.round(n * 100) / 100;

export function money(n) {
  const v = Math.abs(Number(n) || 0);
  if (v === 0) return '$0';
  return `$${v >= 100 ? Math.round(v).toLocaleString('en-US') : v.toFixed(2)}`;
}

export function signedMoney(n) {
  if (!n) return money(0);
  return `${n < 0 ? '−' : '+'}${money(n)}`;
}

/**
 * Which way the last two months went.
 *
 * @param {number|null} twoMonthsAgo  median sale 45-75 days ago
 * @param {number|null} monthAgo      median sale 15-45 days ago
 * @param {number|null} now           today's price
 * @returns {{kind:'climb'|'drop'|'swing'|'flat'|'unknown', next?:number}}
 */
export function trendOf(twoMonthsAgo, monthAgo, now, { salesTwoMonthsAgo = MIN_SALES, salesMonthAgo = MIN_SALES } = {}) {
  const a = Number(twoMonthsAgo);
  const b = Number(monthAgo);
  const c = Number(now);
  if (!(a > 0 && b > 0 && c > 0)) return { kind: 'unknown' };
  if (salesTwoMonthsAgo < MIN_SALES || salesMonthAgo < MIN_SALES) return { kind: 'unknown' };

  const d1 = (b - a) / a;
  const d2 = (c - b) / b;
  const big = (d) => Math.abs(d) >= MOVE;

  if (big(d1) && big(d2) && Math.sign(d1) !== Math.sign(d2)) return { kind: 'swing' };
  // The average monthly change over the two months, carried one month on.
  const next = round2(c + (c - a) / 2);
  if (d1 > 0 && d2 > 0 && (big(d1) || big(d2))) return { kind: 'climb', next };
  if (d1 < 0 && d2 < 0 && (big(d1) || big(d2))) return { kind: 'drop', next };
  return { kind: 'flat' };
}

export function daysHeld(since, now = Date.now()) {
  const t = Date.parse(since);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now - t) / DAY));
}

function profitNote(trend) {
  switch (trend.kind) {
    case 'climb': return `Climbing · likely ${money(trend.next)} next month`;
    case 'swing': return 'Swings a lot · sell while it’s up';
    case 'drop': return 'Dropping · sell before the profit shrinks';
    case 'flat': return 'Steady';
    default: return 'Not enough recent sales to call a trend';
  }
}

function lossNote(trend, days, estimated) {
  if (trend.kind === 'climb') return 'Climbing · could pass what you paid';
  if (days != null && days >= STALE_DAYS) {
    return `In inventory ${estimated ? 'about ' : ''}${days} days · keep out of shows`;
  }
  if (trend.kind === 'drop' || trend.kind === 'swing') return 'Just dipped · your call';
  return 'Below what you paid · your call';
}

/**
 * One row from binder_analysis, judged at a selling rate.
 */
export function judge(row, rate = DEFAULT_RATE, now = Date.now()) {
  const price = row.price_now == null ? null : Number(row.price_now);
  const cost = row.cost_basis == null ? null : Number(row.cost_basis);
  const trend = trendOf(row.price_two_months_ago, row.price_month_ago, price, {
    salesTwoMonthsAgo: row.sales_two_months_ago ?? 0,
    salesMonthAgo: row.sales_month_ago ?? 0,
  });
  const days = daysHeld(row.held_since, now);
  const base = { row, price, cost, trend, days };

  if (price == null) return { ...base, list: 'ask', profit: null, note: 'No price yet' };
  if (cost == null) return { ...base, list: 'ask', profit: null, note: null };

  const profit = round2(price * rate - cost);
  if (profit >= 0) return { ...base, list: 'profit', profit, note: profitNote(trend) };
  return { ...base, list: 'loss', profit, note: lossNote(trend, days, row.held_since_estimated) };
}

/**
 * Every card in the binder, sorted into its list.
 *
 * Profitable cards lead with the most money, losses with the worst, and the
 * cards with no cost with the most valuable, since that is where an unknown
 * loss would hurt most.
 */
export function analyze(rows, rate = DEFAULT_RATE, now = Date.now()) {
  const profit = [];
  const loss = [];
  const ask = [];
  for (const r of rows || []) {
    const j = judge(r, rate, now);
    if (j.list === 'profit') profit.push(j);
    else if (j.list === 'loss') loss.push(j);
    else ask.push(j);
  }
  profit.sort((a, b) => b.profit - a.profit);
  loss.sort((a, b) => a.profit - b.profit);
  ask.sort((a, b) => (b.price ?? -1) - (a.price ?? -1));

  const sum = (xs) => round2(xs.reduce((s, x) => s + x.profit, 0));
  return {
    profit,
    loss,
    ask,
    all: [...profit, ...loss, ...ask],
    profitTotal: sum(profit),
    lossTotal: sum(loss),
  };
}
